import { SortDetails, SortDirection, SortStudents } from "@/enums";
import { StudentPlaylistEntry, StudentPlaylistObjective } from "@/models/StudentPlaylist";
import moment from "moment";

export const sortStudentGroups = (
  sortStudents: {
    sort: SortStudents;
    dir: SortDirection;
  },
  studentPlaylistEntries: StudentPlaylistEntry[]
) => {
  const result = [...studentPlaylistEntries];
  switch (sortStudents.sort) {
    case SortStudents.STUDENTS:
      result.sort((a, b) => {
        const isAsc = sortStudents.dir !== SortDirection.DES;
        // Compare last names first
        if (a.student.last_name < b.student.last_name) return isAsc ? -1 : 1;
        if (a.student.last_name > b.student.last_name) return isAsc ? 1 : -1;

        // If last names are equal, compare first names
        if (a.student.first_name < b.student.first_name) return isAsc ? -1 : 1;
        if (a.student.first_name > b.student.first_name) return isAsc ? 1 : -1;

        return 0;
      });
      break;
    // rest of cases are sorted by numeric value
    default:
      const mapping: Record<number, keyof StudentPlaylistEntry> = {
        [SortStudents.ASSIGNED_TIME]: "assignedTimeMinutes",
        [SortStudents.CORE]: "coreSkills",
        [SortStudents.PREREQ]: "prerequisiteSkills",
        [SortStudents.TEACHER_ASSIGNED]: "teacherAssignedSkills",
        [SortStudents.ROLLOVER]: "rolloverSkills",
      };
      result.sort((a, b) => {
        const key = mapping[sortStudents.sort];
        return sortStudents.dir === SortDirection.DES
          ? (b[key] as number) - (a[key] as number)
          : (a[key] as number) - (b[key] as number);
      });
      break;
  }
  return result;
};

export const sortStudentObjectives = (
  sortDetails: {
    sort: SortDetails;
    dir: SortDirection;
  },
  studentPlaylistEntries: StudentPlaylistEntry[]
) => {
  const result = [...studentPlaylistEntries];
  studentPlaylistEntries.forEach((spe) => {
    switch (sortDetails.sort) {
      case SortDetails.DATE:
        const isAsc = sortDetails.dir !== SortDirection.DES;
        spe.objectives.sort((a, b) => {
          const dateAString = moment(
            a.assignment.dateFrom.split(" ")[0]
          ).format("MM/DD");
          const dateBString = moment(
            b.assignment.dateFrom.split(" ")[0]
          ).format("MM/DD");

          // Split the date string into month and day parts
          const [monthA, dayA] = dateAString.split("/").map(Number);
          const [monthB, dayB] = dateBString.split("/").map(Number);

          // Compare months first
          if (monthA < monthB) return isAsc ? -1 : 1;
          if (monthA > monthB) return isAsc ? 1 : -1;

          // If months are the same, compare days
          if (dayA < dayB) return isAsc ? -1 : 1;
          if (dayA > dayB) return isAsc ? 1 : -1;

          // sort by objective next
          if (a.objective < b.objective) return isAsc ? -1 : 1;
          if (a.objective > b.objective) return isAsc ? 1 : -1;

          return 0;

          // objective used to have 'Objective #:' in front which sorts by that number value
          // const matchA = a.objective.match(/\D*(\d+)\D*/);
          // const matchB = b.objective.match(/\D*(\d+)\D*/);
          // if (!matchA || !matchB) {
          //   return 0;
          // }

          // const aInt = parseInt(matchA[1], 10);
          // const bInt = parseInt(matchB[1], 10);

          // return sortDetails.dir === SortDirection.DES
          //   ? bInt - aInt
          //   : aInt - bInt;
        });
        break;
      case SortDetails.LESSON_SKILL:
        const isLessonSkillAsc = sortDetails.dir !== SortDirection.DES;
        spe.objectives.sort((a, b) => {
          // First, sort by lesson dateFrom (chronological order)
          const lessonDateComparison = a.lesson.dateFrom.localeCompare(b.lesson.dateFrom);
          if (lessonDateComparison !== 0) {
            return isLessonSkillAsc ? lessonDateComparison : -lessonDateComparison;
          }

          // If lessons are the same or have same date, sort by lesson name as fallback
          const lessonNameComparison = a.lesson.name.localeCompare(b.lesson.name);
          if (lessonNameComparison !== 0) {
            return isLessonSkillAsc ? lessonNameComparison : -lessonNameComparison;
          }

          // Within the same lesson, sort by skill sequence
          const aSkillSequence = a.skill.sequence ?? Number.MAX_SAFE_INTEGER;
          const bSkillSequence = b.skill.sequence ?? Number.MAX_SAFE_INTEGER;
          
          if (aSkillSequence !== bSkillSequence) {
            return isLessonSkillAsc ? aSkillSequence - bSkillSequence : bSkillSequence - aSkillSequence;
          }

          // Final fallback: sort by skill name
          const skillNameComparison = (a.skill.displayName || a.skill.skillName || a.skill.skillId || "").localeCompare(
            b.skill.displayName || b.skill.skillName || b.skill.skillId || ""
          );
          return isLessonSkillAsc ? skillNameComparison : -skillNameComparison;
        });
        break;
      case SortDetails.ASSIGNED_TIME:
        spe.objectives.sort((a, b) => {
          return sortDetails.dir === SortDirection.DES
            ? b.timeAssigned - a.timeAssigned
            : a.timeAssigned - b.timeAssigned;
        });
        break;
      // Objective used to have 'Objective #:' in front which this sorts by that number value
      // case SortDetails.OBJECTIVE:
      //   spe.objectives.sort((a, b) => {
      //     const matchA = a.objective.match(/\D*(\d+)\D*/);
      //     const matchB = b.objective.match(/\D*(\d+)\D*/);
      //     if (!matchA || !matchB) {
      //       return 0;
      //     }

      //     const aInt = parseInt(matchA[1], 10);
      //     const bInt = parseInt(matchB[1], 10);

      //     return sortDetails.dir === SortDirection.DES
      //       ? bInt - aInt
      //       : aInt - bInt;
      //   });
      //   break;
      // rest of cases are string sorts
      default:
        spe.objectives.sort((a: any, b: any) => {
          const mapping: Record<number, keyof StudentPlaylistObjective> = {
            [SortDetails.CATEGORY]: "assignmentCategory",
            [SortDetails.MASTERY]: "masteryPacing",
            [SortDetails.STATUS]: "skillAssignedStatus",
            [SortDetails.OBJECTIVE]: "objective",
          };
          const key = mapping[sortDetails.sort];
          const isAsc = sortDetails.dir !== SortDirection.DES;
          if (a[`${key}`] < b[`${key}`]) return isAsc ? -1 : 1;
          if (a[`${key}`] > b[`${key}`]) return isAsc ? 1 : -1;

          return 0;
        });
        break;
    }
  });

  return result;
};
